import { useEffect, useMemo } from "react";
import queryString from "query-string";

import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import { useGetVegaPortalUrl } from "@vega-api/generated/idp-v1/idp-v1";
import { FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { useMarkUserEmailAsActivated } from "@vega-api/generated/user-management-v1/user-management-v1";
import { ActionStatusMessage } from "@vega-core/components/Deprecated/ActionStatusMessage/ActionStatusMessage";
import { OuterPageContainer } from "@vega-core/components/Layout/OuterPageContainer";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { NoticeType } from "@vega-core/components/Notice/Notice";
import { VEGA_ID } from "@vega-core/constants/global";
import { useTheme } from "@vega-core/context/ThemeContext/ThemeContext";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { m } from "@vega-core/paraglide/messages";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { trackEmailActivationViewed } from "@vega-core/utils/posthog/onboardingAnalytics";
import { OktaLoginButton } from "@vega-modules/components/OktaLoginButton/OktaLoginButton";

export const Activate = () => {
  const {
    global: { companyName, companyId },
    theme: { logos },
  } = usePublicConfig();
  const { data: vegaPortalUrl } = useGetVegaPortalUrl();

  const { theme } = useTheme();

  useEffect(() => {
    trackEmailActivationViewed();
  }, []);
  const { email = "" } = queryString.parse(location.search) as {
    email?: string;
  };

  const { isError, error, isSuccess, isLoading } = useMarkUserEmailAsActivated(
    { email },
    {
      query: {
        enabled: isTruthy(email),
      },
    },
  );

  const statusMessage = useMemo(() => {
    if (isFalsy(email)) return m.auth_oktaAccCreateEmailMissing();

    if (isError) {
      const { description } = getErrorMessage(error, {
        defaultMessage: m.auth_oktaAccCreateFail(),
      });

      return description;
    }

    if (isSuccess) return m.auth_oktaAccCreateSuccess();

    return "";
  }, [email, error, isError, isSuccess]);

  const statusVariant = useMemo(() => {
    if (isSuccess) return NoticeType.SUCCESS;

    if (isError || isFalsy(email)) return NoticeType.ERROR;

    return NoticeType.INFO;
  }, [isSuccess, isError, email]);
  const investPortalURL = `${vegaPortalUrl?.lpPortalUrl}login`;

  if (isLoading) {
    return (
      <OuterPageContainer investPortalURL={investPortalURL} isLoading>
        <LoadingSpinner />
      </OuterPageContainer>
    );
  }

  return (
    <OuterPageContainer
      className="justify-center"
      investPortalURL={investPortalURL}
      isLogin
    >
      <div className="mx-auto rounded-lg md:w-98 md:bg-background/primary md:shadow-md md:p-12">
        <div className="flex flex-col h-full gap-4 align-center w-full text-text/primary">
          <div className="flex flex-grow w-full flex-col justify-center p-12 align-center">
            <div className="max-w-lg">
              {companyId === VEGA_ID ? (
                <img
                  alt="vega"
                  className="w-auto h-16 mx-auto lg:mb-6"
                  src={logos.smallLogoLight}
                />
              ) : (
                <img
                  alt="vega"
                  className="w-auto h-8 mx-auto lg:mb-6"
                  src={theme === "light" ? logos.logoLight : logos.logoDark}
                />
              )}
              <h2 className="mt-6 whitespace-nowrap heading-5 flex justify-center">
                {m.general_loginHeader({ companyName })}
              </h2>
              <div className="body-base-regular flex justify-center">
                {m.auth_oktaReSignIn()}
              </div>
              <div className="mt-4">
                <OktaLoginButton
                  source={FrontendPortal.COINVEST_WORKBENCH_PORTAL}
                  variant="primary"
                />
              </div>
            </div>
          </div>
          <ActionStatusMessage message={statusMessage} type={statusVariant} />
        </div>
      </div>
    </OuterPageContainer>
  );
};
